/**
 * Premium "Partner Reveal" — der dramatische Live-Moment.
 *
 * Direkter Counterpart zum `partnerComingSoon`-Teaser: gleiche Card-DNA,
 * gleiche Hierarchie, gleiches Bühnenbild — aber statt Verhüllen wird hier
 * **enthüllt**. Logo + Name starten unscharf (so wie sie der Coming-Soon-
 * Teaser hinterlassen hat) und werden sichtbar live in scharfe, klare
 * Versionen ihrer selbst aufgelöst.
 *
 * Reveal-Choreografie (cinematic, ~4–5 Beats):
 *  1. Card schwebt rein (genauso wie beim Coming-Soon — nahtlose Übergabe).
 *  2. Stamp poppt rein, sagt aber „JETZT LIVE" (statt „COMING SOON").
 *  3. Schloss + „Bald enthüllt"-Hint blenden sanft aus.
 *  4. **Reveal-Beat**: Light-Sweep zieht diagonal über das Logo, gleichzeitig
 *     pulst ein Ring nach außen, ein Sparkle-Burst geht los, und der Card-
 *     Glow flammt kurz auf — währenddessen crossfaded das blurry Layer in
 *     ein scharfes (sowohl beim Logo als auch beim Namen).
 *  5. Sub-Hint wechselt von „Wer wird der nächste?" zu „Frisch im
 *     Marktplatz".
 *  6. Kategorie-Pill + Footer setzen die Botschaft zu Ende.
 *
 * Farb-Theme: standardmäßig Allyver-Primary (Türkis-Grün). Lila/Pink lassen
 * sich weiterhin per `inputs.accent` setzen.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine, makeTl,
  pickMediaSrc, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const partnerReveal: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  // Standardmäßig Allyver-Primary (Grün/Türkis). Falls explizit ein
  // anderer Akzent gesetzt ist (secondary/accent), respektieren wir das.
  const accent = inputs.accent ? accentFor(inputs) : themeColors.primary;
  const isVertical = ctx.format === "9:16";
  const name = (inputs.text ?? "PARTNER").trim();
  const teaser = (inputs.subtext ?? "Jetzt live auf Allyver").trim();
  const branche = String(inputs.number ?? inputs.extras?.category ?? "Dienstleister").trim();
  const logoUrl = pickMediaSrc(inputs.media, 0);

  // Sub-Hint wechselt mit dem Reveal: "Wer wird der nächste?" → "Frisch
  // im Marktplatz". Beides per `extras` überschreibbar, damit Marketing
  // die Texte ohne Code-Eingriff anpassen kann.
  const teaserHintBefore = String(inputs.extras?.hintBefore ?? "Wer wird der nächste Allyver-Partner?").trim();
  const teaserHintAfter = String(inputs.extras?.hintAfter ?? "Frisch im Marktplatz").trim();
  const stampLabel = String(inputs.extras?.stampLabel ?? "Jetzt Live").trim().toUpperCase();

  // ---- Sizes (1:1 zum Coming-Soon-Teaser für nahtlose Übergabe) -----------
  const cardW = isVertical ? 880 : 720;
  const logoArea = isVertical ? 360 : 300;
  const stampSize = typeSize("md", ctx.format) * 0.82;
  const nameSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.66;
  const teaserSize = typeSize("sm", ctx.format) * 0.85;
  const brancheSize = typeSize("sm", ctx.format) * 0.78;
  const hintSize = typeSize("sm", ctx.format) * 0.82;
  const lockHintSize = typeSize("sm", ctx.format) * 0.72;

  // ---- IDs -----------------------------------------------------------------
  const t = makeTl(clipStartSec);
  const cardId = `${clipPrefix}-card`;
  const cardGlowId = `${clipPrefix}-card-glow`;
  const stampWrapId = `${clipPrefix}-stamp-wrap`;
  const stampId = `${clipPrefix}-stamp`;
  const logoSlotId = `${clipPrefix}-logo-slot`;
  const logoBlurId = `${clipPrefix}-logo-blur`;
  const logoSharpId = `${clipPrefix}-logo-sharp`;
  const lockHintId = `${clipPrefix}-lock-hint`;
  const ringId = `${clipPrefix}-ring`;
  const sweepId = `${clipPrefix}-sweep`;
  const burstId = `${clipPrefix}-burst`;
  const nameWrapId = `${clipPrefix}-namewrap`;
  const nameBlurId = `${clipPrefix}-name-blur`;
  const nameSharpId = `${clipPrefix}-name-sharp`;
  const hintBeforeId = `${clipPrefix}-hint-before`;
  const hintAfterId = `${clipPrefix}-hint-after`;
  const brancheId = `${clipPrefix}-branche`;
  const footerId = `${clipPrefix}-foot`;
  const scanId = `${clipPrefix}-scan`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  // ---- Logo: zwei gestapelte Layer ----------------------------------------
  // BLUR-Layer (sichtbar im Reveal-Start, fadet aus) + SHARP-Layer (kommt
  // gleichzeitig rein). Wir animieren nur Opacity → kein GSAP-Filter-Plugin
  // notwendig, läuft auf jeder Engine zuverlässig.
  const logoBlurPx = isVertical ? 24 : 20;
  const sharedImgStyle = "position:absolute;inset:0;margin:auto;max-width:90%;max-height:90%;width:auto;height:auto;object-fit:contain;display:block;";
  const fallbackChar = (id: string, extra: string) =>
    `<div id="${id}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${nameSize * 1.6}px;letter-spacing:-3px;color:${themeColors.fg};${extra}">?</div>`;
  const logoBlurHtml = logoUrl
    ? `<img id="${logoBlurId}" src="${escapeHtml(logoUrl)}" alt="partner-logo-blurred" style="${sharedImgStyle}filter:blur(${logoBlurPx}px) saturate(0.85);opacity:0.88;" />`
    : fallbackChar(logoBlurId, `opacity:0.5;filter:blur(${logoBlurPx}px);`);
  const logoSharpHtml = logoUrl
    ? `<img id="${logoSharpId}" src="${escapeHtml(logoUrl)}" alt="partner-logo" style="${sharedImgStyle}filter:drop-shadow(0 14px 36px ${withAlpha(accent, 0.45)});opacity:0;" />`
    : fallbackChar(logoSharpId, `opacity:0;`);

  // Schloss + „BALD ENTHÜLLT" — startet sichtbar (Hand-off vom Teaser),
  // fadet beim Reveal weg.
  const lockHtml = `<div id="${lockHintId}" style="position:absolute;left:0;right:0;top:50%;transform:translateY(-50%);display:flex;flex-direction:column;align-items:center;gap:10px;color:${withAlpha(themeColors.fg, 0.78)};text-shadow:0 2px 14px rgba(0,0,0,0.65);pointer-events:none;">
    <svg width="${isVertical ? 60 : 52}" height="${isVertical ? 60 : 52}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
    <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lockHintSize}px;letter-spacing:7px;text-transform:uppercase;opacity:0.92;">Bald enthüllt</div>
  </div>`;

  // Ring-Pulse: skaliert beim Reveal nach außen, fadet weg.
  const ringHtml = `<div id="${ringId}" style="position:absolute;inset:0;margin:auto;width:${logoArea * 0.78}px;height:${logoArea * 0.78}px;border-radius:9999px;border:2px solid ${withAlpha(accent, 0.85)};box-shadow:0 0 24px ${withAlpha(accent, 0.55)};opacity:0;pointer-events:none;"></div>`;

  // Light-Sweep: dünner diagonaler Streifen, der einmal über das Logo
  // wandert (klassischer „Premium-Glanz"-Effekt).
  const sweepHtml = `<div id="${sweepId}" style="position:absolute;inset:0;overflow:hidden;border-radius:24px;pointer-events:none;">
    <div style="position:absolute;top:-30%;left:-60%;width:55%;height:160%;transform:rotate(18deg);background:linear-gradient(90deg, transparent 0%, ${withAlpha(themeColors.fg, 0.25)} 45%, ${withAlpha(themeColors.fg, 0.55)} 50%, ${withAlpha(themeColors.fg, 0.25)} 55%, transparent 100%);filter:blur(2px);opacity:0;" id="${sweepId}-bar"></div>
  </div>`;

  // Sparkle-Burst: 8 kleine Punkte rund ums Zentrum, fliegen radial raus.
  const burstCount = 8;
  const burstDots = Array.from({ length: burstCount }).map((_, i) => {
    const ang = (i / burstCount) * Math.PI * 2;
    const dist = logoArea * 0.42;
    const dx = Math.cos(ang) * dist;
    const dy = Math.sin(ang) * dist;
    const size = isVertical ? 8 : 7;
    return `<span class="${burstId}-dot" data-dx="${dx.toFixed(1)}" data-dy="${dy.toFixed(1)}" style="position:absolute;left:50%;top:50%;width:${size}px;height:${size}px;margin-left:-${size / 2}px;margin-top:-${size / 2}px;border-radius:9999px;background:${accent};box-shadow:0 0 14px ${accent}, 0 0 28px ${withAlpha(accent, 0.6)};opacity:0;"></span>`;
  }).join("");
  const burstHtml = `<div id="${burstId}" style="position:absolute;inset:0;pointer-events:none;">${burstDots}</div>`;

  // ---- Name: zwei Layer (blurry + sharp) ----------------------------------
  const nameHtml = `<div id="${nameWrapId}" style="position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;width:100%;gap:14px;">
    <div style="position:relative;display:flex;align-items:center;justify-content:center;width:100%;min-height:${nameSize * 1.05}px;">
      <div id="${nameBlurId}" style="position:absolute;left:0;right:0;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${nameSize}px;line-height:1;letter-spacing:-2.5px;color:${themeColors.fg};text-align:center;filter:blur(20px);opacity:0.92;user-select:none;white-space:nowrap;">${escapeHtml(name)}</div>
      <div id="${nameSharpId}" style="position:relative;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${nameSize}px;line-height:1;letter-spacing:-2.5px;background:linear-gradient(180deg, ${themeColors.fg} 0%, ${withAlpha(themeColors.fg, 0.78)} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;text-shadow:0 0 24px ${withAlpha(accent, 0.25)};text-align:center;opacity:0;user-select:none;white-space:nowrap;">${escapeHtml(name)}</div>
    </div>
    <div style="position:relative;min-height:${hintSize * 1.4}px;display:flex;align-items:center;justify-content:center;">
      <div id="${hintBeforeId}" style="position:absolute;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${hintSize}px;letter-spacing:1.6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.62)};text-align:center;white-space:nowrap;">${escapeHtml(teaserHintBefore)}</div>
      <div id="${hintAfterId}" style="position:relative;font-family:'Inter',sans-serif;font-weight:${weights.black};font-size:${hintSize}px;letter-spacing:2.2px;text-transform:uppercase;color:${accent};text-align:center;opacity:0;white-space:nowrap;text-shadow:0 0 18px ${withAlpha(accent, 0.45)};">${escapeHtml(teaserHintAfter)}</div>
    </div>
  </div>`;

  // ---- Kategorie-Pill ------------------------------------------------------
  const brancheHtml = `<div id="${brancheId}" style="display:inline-flex;align-items:center;gap:12px;padding:12px 24px;border-radius:9999px;background:${withAlpha(accent, 0.2)};border:1px solid ${withAlpha(accent, 0.55)};box-shadow:0 6px 22px ${withAlpha(accent, 0.28)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${brancheSize}px;letter-spacing:2.8px;text-transform:uppercase;color:${accent};">
    <span style="opacity:0.65;">Kategorie</span><span style="opacity:0.45;">·</span><span>${escapeHtml(branche)}</span>
  </div>`;

  // ---- LIVE-Stempel (statt COMING SOON) -----------------------------------
  // Pulsierender Punkt + scharfer Akzent-Rand → liest sich sofort als „on air".
  const stampHtml = `<div id="${stampWrapId}" style="position:absolute;top:-22px;left:50%;z-index:5;display:flex;flex-direction:column;align-items:center;">
    <div id="${stampId}" style="position:relative;display:inline-flex;align-items:center;gap:12px;padding:14px 28px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 14px 42px ${withAlpha(accent, 0.6)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${stampSize}px;letter-spacing:4px;text-transform:uppercase;color:${themeColors.bg};">
      <span style="position:relative;width:11px;height:11px;border-radius:9999px;background:${themeColors.bg};box-shadow:0 0 12px ${themeColors.bg};animation:hyper-live 1.4s ease-in-out infinite;"></span>
      <span>${escapeHtml(stampLabel)}</span>
    </div>
  </div>`;

  // ---- Footer (JETZT EXKLUSIV / Live-Botschaft) ---------------------------
  const footerHtml = `<div id="${footerId}" style="display:inline-flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${teaserSize}px;letter-spacing:5px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.9)};">
    <span style="width:28px;height:2px;background:${accent};"></span>
    <span>${escapeHtml(teaser)}</span>
    <span style="width:28px;height:2px;background:${accent};"></span>
  </div>`;

  const scanHtml = `<div id="${scanId}" style="position:absolute;inset:0;pointer-events:none;background-image:repeating-linear-gradient(0deg, transparent 0, transparent 4px, ${withAlpha(themeColors.fg, 0.025)} 4px, ${withAlpha(themeColors.fg, 0.025)} 5px);mix-blend-mode:overlay;opacity:0.5;"></div>`;

  const padTopBot = isVertical ? 80 : 56;
  const cardPadV = isVertical ? 64 : 48;
  const cardPadH = isVertical ? 52 : 40;

  const html = `<style>
    @keyframes hyper-live{0%,100%{opacity:0.4;transform:scale(0.85);}50%{opacity:1;transform:scale(1.05);}}
  </style>
${scanHtml}
<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:28px;padding:${padTopBot}px 60px;">
  <div id="${cardId}" style="position:relative;width:${cardW}px;max-width:92%;border-radius:36px;background:linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.06)} 0%, ${withAlpha(themeColors.fg, 0.015)} 100%);border:1px solid ${withAlpha(themeColors.fg, 0.14)};box-shadow:0 40px 100px rgba(0,0,0,0.55), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.16)};backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);padding:${cardPadV}px ${cardPadH}px;display:flex;flex-direction:column;align-items:center;gap:30px;">
    <div id="${cardGlowId}" aria-hidden="true" style="position:absolute;inset:-2px;border-radius:38px;background:linear-gradient(135deg, ${withAlpha(accent, 0.6)} 0%, transparent 35%, transparent 65%, ${withAlpha(accent, 0.45)} 100%);opacity:0;pointer-events:none;mix-blend-mode:screen;filter:blur(2px);"></div>
    ${stampHtml}

    <div id="${logoSlotId}" style="position:relative;width:100%;height:${logoArea}px;border-radius:24px;display:flex;align-items:center;justify-content:center;padding:18px;overflow:hidden;">
      ${logoBlurHtml}
      ${logoSharpHtml}
      ${lockHtml}
      ${sweepHtml}
      ${ringHtml}
      ${burstHtml}
    </div>

    ${nameHtml}
    ${brancheHtml}
  </div>
  ${footerHtml}
</div>`;

  // ---- Animation -----------------------------------------------------------
  // Init: Card + Stempel kommen rein wie beim Coming-Soon. Das Logo
  // **startet bewusst im Coming-Soon-Endzustand** (blurry sichtbar +
  // Schloss sichtbar), damit der Reveal direkt aus dem Teaser fortsetzt.
  const init = [
    gsapSetLine(`#${cardId}`, { opacity: 0, y: 60, scale: 0.94 }),
    gsapSetLine(`#${cardGlowId}`, { opacity: 0 }),
    gsapSetLine(`#${stampWrapId}`, { opacity: 0, scale: 0.4, rotation: -10, xPercent: -50 }),
    gsapSetLine(`#${logoSlotId}`, { opacity: 0, scale: 0.94 }),
    gsapSetLine(`#${logoBlurId}`, { opacity: 0.88 }),
    gsapSetLine(`#${logoSharpId}`, { opacity: 0, scale: 1.05 }),
    gsapSetLine(`#${lockHintId}`, { opacity: 1, y: 0 }),
    gsapSetLine(`#${ringId}`, { opacity: 0, scale: 0.55 }),
    gsapSetLine(`#${sweepId}-bar`, { opacity: 0, x: -200 }),
    gsapSetLine(`#${nameWrapId}`, { opacity: 0, y: 12 }),
    gsapSetLine(`#${nameBlurId}`, { opacity: 0.92 }),
    gsapSetLine(`#${nameSharpId}`, { opacity: 0, scale: 1.04 }),
    gsapSetLine(`#${hintBeforeId}`, { opacity: 1 }),
    gsapSetLine(`#${hintAfterId}`, { opacity: 0, y: 6 }),
    gsapSetLine(`#${brancheId}`, { opacity: 0, y: 14, scale: 0.9 }),
    gsapSetLine(`#${footerId}`, { opacity: 0, y: 12 }),
    gsapSetLine(`.${burstId}-dot`, { opacity: 0, scale: 0.4, x: 0, y: 0 }),
  ].join("\n");

  // Reveal-Punkt (Sekunde, ab der das Logo scharf wird).
  const revealAt = 1.55;
  const tweens: string[] = [
    // Phase 1 — Übergabe vom Coming-Soon (Card + Stempel + Logo-Slot rein).
    t.to(`#${cardId}`, { opacity: 1, y: 0, scale: 1, duration: 0.95, ease: "raw:\"power3.out\"" }, 0.05),
    t.fromTo(`#${cardGlowId}`, { opacity: 0 }, { opacity: 0.55, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.2),
    t.to(`#${cardGlowId}`, { opacity: 0.18, duration: 0.7, ease: "raw:\"power2.inOut\"" }, 0.85),
    t.to(`#${logoSlotId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"back.out(1.4)\"" }, 0.5),
    t.to(`#${stampWrapId}`, { opacity: 1, scale: 1, rotation: -6, duration: 0.6, ease: "raw:\"back.out(2.2)\"" }, 0.7),
    t.to(`#${nameWrapId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.95),

    // Phase 2 — Reveal-Beat (alles passiert in ~0.9s rund um `revealAt`).
    // Schloss-Hint fadet weg, Ring pulst raus, Glow flammt auf.
    t.to(`#${lockHintId}`, { opacity: 0, y: -8, duration: 0.4, ease: "raw:\"power2.in\"" }, revealAt - 0.2),
    t.fromTo(`#${ringId}`, { opacity: 0.95, scale: 0.55 }, { opacity: 0, scale: 1.7, duration: 0.85, ease: "raw:\"power2.out\"" }, revealAt - 0.05),
    t.fromTo(`#${cardGlowId}`, { opacity: 0.18 }, { opacity: 0.85, duration: 0.25, ease: "raw:\"power2.out\"" }, revealAt - 0.05),
    t.to(`#${cardGlowId}`, { opacity: 0.22, duration: 0.7, ease: "raw:\"power2.inOut\"" }, revealAt + 0.25),

    // Light-Sweep wandert einmal über das Logo.
    t.to(`#${sweepId}-bar`, { opacity: 1, duration: 0.05, ease: "raw:\"none\"" }, revealAt - 0.05),
    t.to(`#${sweepId}-bar`, { x: 1100, duration: 0.7, ease: "raw:\"power2.inOut\"" }, revealAt - 0.05),
    t.to(`#${sweepId}-bar`, { opacity: 0, duration: 0.2, ease: "raw:\"power1.in\"" }, revealAt + 0.55),

    // Crossfade Logo blurry → sharp.
    t.to(`#${logoBlurId}`, { opacity: 0, duration: 0.55, ease: "raw:\"power2.inOut\"" }, revealAt + 0.05),
    t.to(`#${logoSharpId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"power2.out\"" }, revealAt + 0.05),

    // Crossfade Name blurry → sharp (leicht versetzt für Hierarchie).
    t.to(`#${nameBlurId}`, { opacity: 0, duration: 0.5, ease: "raw:\"power2.inOut\"" }, revealAt + 0.2),
    t.to(`#${nameSharpId}`, { opacity: 1, scale: 1, duration: 0.65, ease: "raw:\"back.out(1.6)\"" }, revealAt + 0.2),

    // Sub-Hint wechselt: „Wer wird der nächste?" → „Frisch im Marktplatz".
    t.to(`#${hintBeforeId}`, { opacity: 0, y: -6, duration: 0.35, ease: "raw:\"power2.in\"" }, revealAt + 0.1),
    t.to(`#${hintAfterId}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, revealAt + 0.45),

    // Phase 3 — Tail (Kategorie-Pill + Footer).
    t.to(`#${brancheId}`, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "raw:\"back.out(1.6)\"" }, revealAt + 0.85),
    t.to(`#${footerId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, revealAt + 1.1),
  ];

  // Sparkle-Burst: 8 Punkte fliegen radial raus, jeweils leicht gestaffelt.
  // Wir generieren explizite Tweens (statt CSS-Vars) damit GSAP die `data-*`
  // Werte gar nicht braucht — wir schreiben die Endpunkte direkt rein.
  for (let i = 0; i < burstCount; i++) {
    const ang = (i / burstCount) * Math.PI * 2;
    const dist = logoArea * 0.55;
    const dx = +(Math.cos(ang) * dist).toFixed(1);
    const dy = +(Math.sin(ang) * dist).toFixed(1);
    const sel = `.${burstId}-dot:nth-child(${i + 1})`;
    const at = revealAt + 0.02 + (i % 4) * 0.04;
    tweens.push(
      t.to(sel, { opacity: 1, scale: 1.4, duration: 0.18, ease: "raw:\"power2.out\"" }, at),
      t.to(sel, { x: dx, y: dy, scale: 0.6, opacity: 0, duration: 0.7, ease: "raw:\"power2.out\"" }, at + 0.05),
    );
  }

  // Sanftes Schweben der gesamten Card am Ende.
  const dwellStart = revealAt + 1.4;
  const dwell = Math.max(0.6, clipDurationSec - dwellStart - 0.6);
  tweens.push(t.to(`#${cardId}`, { y: -6, duration: dwell, ease: "raw:\"sine.inOut\"" }, dwellStart));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
